// Import from NPM
// -------------------------------------
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Dropdown, Label } from "semantic-ui-react";

// Import from Config
// -------------------------------------
import {
    apiV6,
    useSendTrackerMutation,
    useGetSessionsQuery,
    useGetUnreadCountQuery,
} from "@api/apiV6";
import { trackClear } from "@reducers/trackerSlice";
import { clearAttempt } from "@reducers/attemptSlice";

import { darkStyle, text_truncate } from "@utilities/helpers";
import { Div, Icon } from "@components/Generics.react";
import "@styles/navbars.scss";
import { settings } from "@config/settings/app.settings";

// Import Components
// -------------------------------------
import UserAvatar from "./UserAvatar.react";
import SearchBar from "./SearchBar.react";
import Notifications from "./Notifications.react";

export default function Navbar(props) {
    const { logo, name, rootPage = false, isolated = false } = props;
    const user = useSelector((state) => state.auth.user);
    const tracker = useSelector((state) => state.tracker);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { t } = useTranslation("common");

    const username = `${user?.first_name} ${user?.last_name}`;
    const isPortrait = window.innerWidth < window.innerHeight;

    const [notifications, setNotifications] = useState(false);
    const { data: unread } = useGetUnreadCountQuery(user._id);
    //---------------------------------------------------------------------------
    // Sync Data with server when the tracker is set to pushable or when called manually. Also sync sessions.
    // Please Note: This makes the Navbar a control element. It must always be included in all pages.
    //---------------------------------------------------------------------------
    useGetSessionsQuery(user._id);
    const [sendTracker] = useSendTrackerMutation();

    useEffect(() => {
        (async () => {
            if (tracker.pushable) await syncData();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tracker.pushable]);

    async function syncData() {
        try {
            await sendTracker({ trackers: tracker }).unwrap();
            await dispatch(trackClear());
            await dispatch(clearAttempt());
        } catch (err) {
            console.error(`Failed to send trackers to server. Reason: ${err}`);
        }
    }
    async function resyncData() {
        try {
            await syncData();
            await dispatch(apiV6.util.resetApiState());
            navigate("/");
        } catch (err) {
            console.error(`Failed to send trackers to server. Reason: ${err}`);
        }
    }

    // ========================= Render Function =================================
    return (
        <Div
            fluid
            className="qd-nav"
            style={
                isPortrait &&
                !settings.ui.flatMobileHeader &&
                darkStyle(settings.colors.main)
            }
        >
            <Div fullht className="nav-inner" padded={!isPortrait}>
                {isPortrait && !rootPage && (
                    <Icon
                        name="chevron left"
                        large
                        float-left
                        fitted
                        padTop
                        onClick={() => navigate(-1)}
                    />
                )}
                <Div
                    float-left
                    rounded={!isPortrait}
                    white
                    className="logo-block"
                    onClick={() => navigate(isolated ? "#" : "/")}
                >
                    <img
                        src={logo || "/assets/images/configurable/logo.png"}
                        alt="Brand Logo"
                        className="logo"
                    />
                </Div>
                <Div padded={!isPortrait} fullht className="pagename">
                    {isolated && !isPortrait ? (
                        <Div padTop altText uppercase>
                            {name ? name : t("appName")}
                        </Div>
                    ) : (
                        <Div inline altText>
                            {name ? name : t("appName")}
                        </Div>
                    )}
                    {!isPortrait && !rootPage && (
                        <Div
                            fit-content
                            padded
                            ivory
                            snubbed
                            small
                            clickable
                            hoverFade
                            slightShadow
                            nudgeRight
                            txtHalf
                            onClick={() => navigate(-1)}
                        >
                            <Icon name="chevron left" />
                        </Div>
                    )}
                </Div>
                <Div float-right>
                    {!isPortrait && !isolated && (
                        <Div inlineBlock nudgeRight>
                            <SearchBar />
                        </Div>
                    )}
                    <Div inlineBlock nudgeLeft padTop fitted>
                        {!isolated && (
                            <Dropdown
                                inline
                                trigger={
                                    <Div>
                                        <Icon name="bell" />
                                        {unread > 0 && (
                                            <Label
                                                color="blue"
                                                size="mini"
                                                floating
                                            >
                                                {unread.count}
                                            </Label>
                                        )}
                                    </Div>
                                }
                                className="notification-icon"
                                direction="left"
                                scrolling
                                onClick={() => setNotifications(true)}
                                onClose={() => setNotifications(false)}
                            >
                                <Dropdown.Menu>
                                    {notifications ? (
                                        <Notifications />
                                    ) : (
                                        <Div />
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                        <span className="username">
                            {text_truncate(username, 18)}
                        </span>
                        <UserAvatar
                            name={username}
                            image={user.image}
                            avatarConfig={user.avatarConfig}
                            size={32}
                        />
                        <Dropdown
                            pointing
                            direction="left"
                            icon="ellipsis vertical"
                            className="ellipsis"
                        >
                            <Dropdown.Menu>
                                <Dropdown.Item
                                    icon={"pencil"}
                                    text={"Edit Profile"}
                                    onClick={() =>
                                        navigate(
                                            `/editProfile${
                                                isolated ? "/update" : "/avatar"
                                            }`
                                        )
                                    }
                                />
                                {location.pathname === "/" && (
                                    <Dropdown.Item
                                        icon={"sync"}
                                        text={"Sync Data"}
                                        onClick={resyncData}
                                    />
                                )}
                                {/* {isPortrait && (
                                    <Dropdown.Item
                                        icon={"headphones"}
                                        text={"Need Help?"}
                                        onClick={() =>
                                            navigate("/app/helpdesk")
                                        }
                                    />
                                )} */}
                                <Dropdown.Item
                                    icon={"log out"}
                                    text={"Log out"}
                                    onClick={() => navigate("/logout")}
                                />
                            </Dropdown.Menu>
                        </Dropdown>
                    </Div>
                </Div>
            </Div>
        </Div>
    );
}
